import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React, { useEffect, useRef } from 'react';

// Simple component that renders SVG with proper namespace handling
function SVGPassthroughView({ node }: any) {
    const svgHtml = node.attrs.html || '';
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current && svgHtml) {
            console.log('[SVGPassthrough] Rendering SVG, encoded length:', svgHtml.length);

            // Clear previous content
            containerRef.current.innerHTML = '';

            try {
                // Decode base64-encoded SVG
                // The SVG is base64 encoded to prevent corruption during HTML attribute storage
                let decodedSVG: string;
                try {
                    decodedSVG = decodeURIComponent(escape(atob(svgHtml)));
                    console.log('[SVGPassthrough] Decoded SVG length:', decodedSVG.length);
                } catch (decodeError) {
                    console.error('[SVGPassthrough] Base64 decode error:', decodeError);
                    containerRef.current.textContent = 'SVG Decode Error';
                    return;
                }

                // Parse SVG with proper namespace using DOMParser
                const parser = new DOMParser();
                const doc = parser.parseFromString(decodedSVG, 'image/svg+xml');
                const svgElement = doc.documentElement;

                // Check for parse errors
                const parserError = doc.querySelector('parsererror');
                if (parserError) {
                    console.error('[SVGPassthrough] Parse error:', parserError.textContent);
                    containerRef.current.textContent = 'SVG Parse Error';
                    return;
                }

                // Ensure proper SVG element with namespace
                if (svgElement && svgElement.tagName.toLowerCase() === 'svg') {
                    // Ensure xmlns is set
                    svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

                    // Import node with proper namespace
                    const importedSvg = document.importNode(svgElement, true);
                    containerRef.current.appendChild(importedSvg);

                    console.log('[SVGPassthrough] ✓ SVG rendered successfully');
                } else {
                    console.error('[SVGPassthrough] No valid SVG element found');
                    containerRef.current.textContent = 'Invalid SVG';
                }
            } catch (err) {
                console.error('[SVGPassthrough] Error rendering SVG:', err);
                containerRef.current.textContent = 'SVG Render Error';
            }
        }
    }, [svgHtml]);

    return (
        <NodeViewWrapper>
            <div
                ref={containerRef}
                contentEditable={false}
                suppressContentEditableWarning
            />
        </NodeViewWrapper>
    );
}

/**
 * Dead simple SVG extension - just catches SVG tags and renders them as-is
 * No processing, no wrapping, no fancy features - just pass through
 */
export const SVGPassthrough = Node.create({
    name: 'svgPassthrough',
    group: 'block',
    atom: true,
    draggable: false,

    addAttributes() {
        return {
            html: {
                default: '',
            },
        };
    },

    parseHTML() {
        console.log('[SVGPassthrough] parseHTML rules registered');
        return [
            {
                // Catch raw SVG tags (from LLM/initial load)
                tag: 'svg',
                priority: 100, // High priority to override other parsers
                getAttrs: (dom) => {
                    console.log('[SVGPassthrough] ⭐ Parsing raw SVG tag');
                    if (dom instanceof Element) {
                        const html = dom.outerHTML;
                        console.log('[SVGPassthrough] Captured SVG HTML length:', html.length);

                        // Base64 encode to prevent corruption
                        const encoded = btoa(unescape(encodeURIComponent(html)));
                        console.log('[SVGPassthrough] Encoded to base64, length:', encoded.length);

                        return {
                            html: encoded,
                        };
                    }
                    console.warn('[SVGPassthrough] ⚠️ SVG tag found but not Element instance');
                    return false; // Return false if not valid
                },
            },
            {
                // Catch our own wrapper div (from saved content)
                tag: 'div[data-svg-passthrough]',
                priority: 100, // High priority
                getAttrs: (dom) => {
                    console.log('[SVGPassthrough] ⭐ Parsing saved SVG wrapper');
                    if (dom instanceof HTMLElement) {
                        const encoded = dom.getAttribute('data-svg-html') || '';
                        console.log('[SVGPassthrough] Restored base64 SVG, encoded length:', encoded.length);

                        // Already base64 encoded, pass it through as-is
                        return {
                            html: encoded,
                        };
                    }
                    console.warn('[SVGPassthrough] ⚠️ Wrapper div found but not HTMLElement instance');
                    return false; // Return false if not valid
                },
            },
        ];
    },

    renderHTML({ node }) {
        // Output wrapper div with SVG stored as data attribute
        // The actual rendering is handled by the NodeView component
        return ['div', {
            'data-svg-passthrough': '',
            'data-svg-html': node.attrs.html
        }];
    },

    addNodeView() {
        return ReactNodeViewRenderer(SVGPassthroughView);
    },
});
